import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Badge } from 'components/Badge';
import { Button } from 'components/Button';
import { FilterControls, type FilterOption } from 'components/FiltersControls';
import { Heading } from 'components/Heading';
import { Icon } from 'components/Icon';
import { InlineCode } from 'components/InlineCode';
import { ModalButton } from 'components/ModalButton';
import { AdvancedTable } from 'components/Table';
import { TinySkinPreview } from 'components/TinySkinPreview';
import { emitMessage, useIsAdmin } from 'engine/api';
import { appConfig } from 'engine/appConfig';
import { GameTypeOptions } from 'forms/GameFormConstants';
import { Archive, ArchiveRestore, Eye, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import type { EventView } from 'shared/modelTypes/event';
import { toSentenceCase } from 'shared/util';

export const Route = createFileRoute('/dashboard/events/')({
	component: RouteComponent,

	loader: async (ctx) => {
		const archived = 'archived' in ctx.location.search ? ctx.location.search?.archived == true /* Cast to boolean */ : false;

		const data = await emitMessage({ kind: 'get-event-list', includeArchived: archived });

		return data;
	},
});

function RouteComponent() {
	const data = Route.useLoaderData();
	const isAdmin = useIsAdmin();
	const router = useRouter();

	const searchParams = new URLSearchParams(location.search);
	const showArchived = searchParams.get('archived') == 'true';

	const [events, setEvents] = useState(data.eventViews);
	const changeArchivedToggle = (val: boolean) => {
		router.navigate({ to: '/dashboard/events', search: { archived: val } });
	};

	const statusLookup = { ended: 'warn', open: 'info', started: 'success', draft: 'info' } as const;

	const filterOptions: FilterOption<EventView>[] = [
		{
			key: 'game',
			label: 'Game',
			values: (data?.gameViews?.sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })) || []).map((g) => ({
				label: g.name,
				value: g.id,
			})),
			getValue: (game) => game.gameId,
		},

		{
			key: 'status',
			label: 'Status',
			values: ['draft', 'open', 'started', 'ended'].map((s) => ({ label: toSentenceCase(s), value: s })),
			getValue: (e) => e.status,
		},
		{
			key: 'lang',
			label: 'lang',
			values: (data.languages ?? ['nl', 'en']).map((language) => ({
				label: `${language}`,
				value: language,
			})),
			getValue: (variant) => {
				const game = data?.gameMap?.[variant.gameId];

				return game?.language || '';
			},
		},
	];

	if (GameTypeOptions.length > 1) {
		filterOptions.push({
			key: 'type',
			label: 'Type',
			values: GameTypeOptions.map((x) => ({ value: x.label, label: x.label })),
			getValue: (e) => e.eventType ?? '',
		});
	}

	if (appConfig.permissions.allowVariants)
		filterOptions.push({
			key: 'variant',
			label: 'Variant',
			values:
				(data?.variantViews?.sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })) || [])
					.map((v) => ({ label: v.name, value: v.name }))
					.filter((v, i, arr) => arr.findIndex((x) => x.value === v.value) === i) || [],
			getValue: (event) => {
				const variant = data?.variantMap?.[event.variantId!];
				return variant?.name || '';
			},
		});

	console.log(events);
	return (
		<div className="flex flex-col gap-3">
			<div className="border-yellow flex items-center gap-3">
				<Heading>Events</Heading>

				<Button to="/dashboard/events/new">
					New Event <Plus className="size-4" />
				</Button>
				<Button mode="transparent" size="sm" onClick={() => changeArchivedToggle(!showArchived)}>
					{!showArchived ? 'Archived' : 'Active'}
					<Icon icon={!showArchived ? Archive : ArchiveRestore} className="size-4" />
				</Button>
			</div>
			<FilterControls
				items={data.eventViews || []}
				getSearchableText={(s) => s.name}
				onChanged={setEvents}
				sortOptions={[
					{
						key: 'createdAt',
						label: 'Creation date',
						sortFn: (a, b) => a.createdAt.localeCompare(b.createdAt),
					},
					{
						key: 'updatedAt',
						label: 'Last updated',
						sortFn: (a, b) => a.updatedAt.localeCompare(b.updatedAt),
					},
					{
						key: 'name',
						label: 'Name',
						sortFn: (a, b) => a.name.localeCompare(b.name),
					},
					{
						key: 'code',
						label: 'Code',
						sortFn: (a, b) => a.code?.localeCompare(b.code ?? '') ?? 0,
					},
				]}
				filterOptions={filterOptions}
			/>

			{!data.eventViews && <span>Could not get events.</span>}
			{events && (
				<AdvancedTable
					className="w-fit"
					headers={[
						{ name: 'Name' },
						{ name: 'Code' },
						{ name: 'Date', hideOnMobile: true },
						{ name: 'Game', hideOnMobile: true },
						{ name: 'Type', hideOnMobile: true },
						{ name: 'Variant', hideOnMobile: true },
						{ name: 'Lang', hideOnMobile: true },
						{ name: 'Skin', hideOnMobile: true },
						{ name: 'Actions' },
					].map((x) => {
						const clearedObj = { name: '', hideOnMobile: true };
						const name = x.name.toLowerCase();
						if (!appConfig.permissions.allowSkins && name == 'skin') return clearedObj;
						if (!appConfig.permissions.allowVariants && name == 'variant') return clearedObj;

						return x;
					})}
					rows={events.map((e) => {
						const variant = e.variantId ? data?.variantMap?.[e.variantId] : undefined;

						return [
							// ----------- NAME -----------
							<span className="font-bold">
								{e.name}
								<br />
								<Badge status={statusLookup[e.status]}>{toSentenceCase(e.status)}</Badge>
							</span>,
							// ----------- CODE -----------
							<>
								{e.code && (
									<InlineCode clipboardButton={true} value={e.code}>
										{e.code}
									</InlineCode>
								)}
								{!e.code && <small>Event has no code</small>}
							</>,
							// ----------- STARTS AT -----------
							<span>{e.date}</span>,
							// ----------- GAME -----------
							<span>{e.gameName}</span>,
							// ----------- TYPE -----------
							<span>{e.eventType}</span>,
							// ----------- VARIANT -----------
							appConfig.permissions.allowVariants ? <>{variant ? <span>{variant.name}</span> : <small>-</small>}</> : <></>,
							// ---------- Language ----------
							<span className="text-md size-full justify-center text-center align-middle">
								<img className="size-4" src={`/flags/${e.lang}.svg`} />
							</span>,
							// ----------- SKIN -----------
							appConfig.permissions.allowSkins ? <TinySkinPreview skin={e.skin?.skin} /> : <></>,
							// ----------- ACTIONS -----------
							<div className="flex gap-1">
								<Button to={'/dashboard/events/view/$id'} params={{ id: e.id }} mode={'primary'} className="p-1">
									<Eye />
								</Button>
								<Button
									to={'/dashboard/events/edit/$id'}
									params={{ id: e.id }}
									mode={'secondary'}
									className="border-2 border-gray-200 p-1"
								>
									<Pencil />
								</Button>
								{isAdmin && (
									<ModalButton
										title={`Are you sure you want to ${!e.archived ? 'archive' : 'unarchive'} "${e.name}"?`}
										onAccept={async () => {
											await emitMessage({ kind: 'event-archive', id: e.id, archive: !e.archived });

											router.navigate({ to: '/dashboard/events', search: { archived: showArchived } });
										}}
										mode={'secondary'}
										className="border-2 border-gray-200 p-1"
									>
										{e.archived ? <ArchiveRestore /> : <Archive />}
									</ModalButton>
								)}
							</div>,
						];
					})}
				></AdvancedTable>
			)}
		</div>
	);
}
